import { ACTIVITY_ENTITY_TYPES, type ActivityEntityType } from "./event-types.js";

/**
 * Where a feed row leads — expressed as a VALUE, never as a URL.
 *
 * The `audit` package names no route and imports no router. `apps/app` turns one of these into a
 * location in a single exhaustive switch, which is the only place in the codebase that knows both an
 * activity destination and a path.
 */
export type ModuleName =
  | "savings"
  | "properties"
  | "investments"
  | "expenses"
  | "cards"
  | "income"
  | "vehicles"
  | "settings";

export type ActivityDestination =
  /** A record with a page of its own. Three entity types have one. */
  | { kind: "record"; module: "savings" | "properties" | "investments"; recordId: string }
  /** The module page that owns the record. Nine entity types resolve here. */
  | { kind: "module"; module: ModuleName }
  /** Not activatable. The only inert outcome, and reachable only through a missing `entity_id`. */
  | { kind: "none" };

/**
 * What each entity type resolves to, before an `entity_id` is considered.
 *
 * `recordModule` means "this entity has a page of its own, and the id names it".
 * `module` means "this entity lives on a list page; the id has nowhere to go".
 */
type DestinationRule =
  | { via: "record"; module: "savings" | "properties" | "investments" }
  | { via: "module"; module: ModuleName };

/**
 * ==========================================================================================
 * A DESTINATION IS A PLACE, NOT NECESSARILY A RECORD
 * ==========================================================================================
 *
 * AC-AL9 originally said "the detail view of the affected record". Detail routes exist for exactly three
 * of the twelve entity types — `savings_/$accountId`, `properties_/$propertyId`, `investments_/$stockId`.
 * The other nine modules are list pages with dialogs, so there is no detail view to lead to, and the
 * honest destination is the page the record lives on. `activity-log.md` was amended to say so rather than
 * left describing an application that does not exist (Principle I).
 *
 * Two alternatives were considered and rejected — see research R2. Deep-linking with a row highlight
 * would add search-param validation to six routes and a scroll-and-highlight affordance to five domain
 * UI packages this feature otherwise does not touch, and would still have nothing to highlight for a
 * stock transaction, which lives on the stock's page rather than in the list. Resolving child rows
 * through their parents would have this read model joining four other contexts' tables and quadruple the
 * per-page query budget, to reach pages the module rule already reaches.
 *
 * `Record<ActivityEntityType, DestinationRule>` is what makes this total: a fifteenth entity type
 * without a rule is a compile error here, not a row that looks activatable and does nothing.
 */
const ENTITY_DESTINATIONS: Record<ActivityEntityType, DestinationRule> = {
  // The three with pages of their own.
  savings_account: { via: "record", module: "savings" },
  property: { via: "record", module: "properties" },
  stock: { via: "record", module: "investments" },

  // The eleven without.
  expense: { via: "module", module: "expenses" },
  card: { via: "module", module: "cards" },
  income_source: { via: "module", module: "income" },
  income_source_amount: { via: "module", module: "income" },
  income_entry: { via: "module", module: "income" },
  /** The transaction lives on its stock's page, which needs the STOCK's id — and the row carries the
   * transaction's. Resolving one from the other is a query per row; the list is where the member can
   * find it themselves. */
  stock_transaction: { via: "module", module: "investments" },
  /** A mortgage part belongs to a property, and carries its own id for the same reason. This is also
   * where the two `expense.attribution_*` events land: their `entity_type` is `mortgage`, because a
   * derived line has no `expenses` row — so keying on the event name's prefix would send a member to
   * Expenses to look for something that was never there. */
  mortgage: { via: "module", module: "properties" },
  member: { via: "module", module: "settings" },
  household: { via: "module", module: "settings" },
  /*
   * BOTH VEHICLE TYPES RESOLVE TO THE MODULE, and `vehicle` was `via: "record"` for an hour.
   *
   * 023 ships no vehicle DETAIL route — the module page lists them — so a record destination would
   * have this catalogue describing an application that does not exist, which is exactly the
   * divergence Principle I forbids. 018's own rule covers it: "a destination is a place, not
   * necessarily a record."
   *
   * Promote `vehicle` to `via: "record"` in the same change that adds `/vehicles/$vehicleId`, and
   * not before.
   */
  vehicle: { via: "module", module: "vehicles" },
  vehicle_valuation: { via: "module", module: "vehicles" },
};

const KNOWN = new Set<string>(ACTIVITY_ENTITY_TYPES);

/**
 * The destination for a row, from its two columns and nothing else.
 *
 * ==========================================================================================
 * THE `entity_id` RULE COMES FIRST, AND IS UNCONDITIONAL
 * ==========================================================================================
 *
 * No id, no destination — whatever the entity type. Stated as a rule rather than as a list of exceptions
 * so that a future event which omits `entity_id` inherits it instead of needing one.
 *
 * It is what renders `investment.prices_fetched` inert: that row summarises a RUN, and a run is not a
 * record. It is also what makes `household.created` inert, its subject being the household the row is
 * already scoped to.
 *
 * ==========================================================================================
 * EXISTENCE IS NOT CHECKED, DELIBERATELY
 * ==========================================================================================
 *
 * This function does not know whether the record is still there and must not try. Checking would be a
 * query per row across twelve tables on every page — and the page's whole budget is one query. A record
 * that has since been hard-deleted (in practice only a stock removed while it had no transactions) is
 * still linked, and the destination page reports that it is gone. FR-015 and FR-016.
 *
 * Takes `entityType: string` because that is what the column hands over. An unrecognised value yields
 * `{ kind: "none" }`, a path `activity-catalogue.test.ts` makes unreachable.
 */
export function destinationFor(entityType: string, entityId: string | null): ActivityDestination {
  if (entityId === null || entityId === "") return { kind: "none" };
  if (!KNOWN.has(entityType)) return { kind: "none" };

  const rule = ENTITY_DESTINATIONS[entityType as ActivityEntityType];

  return rule.via === "record"
    ? { kind: "record", module: rule.module, recordId: entityId }
    : { kind: "module", module: rule.module };
}

/** Exported for the set-equality test, which asserts this map covers the database enum exactly. */
export const ENTITY_DESTINATION_KEYS = Object.keys(ENTITY_DESTINATIONS);
